import { PhotoSquareOutlineIcon } from '@carboncopyco/icons';
import type { Tour } from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useTourDispatch, useTourSelector } from '../../../../../../hooks';
import useImageProps from '../../../../../../hooks/useImageProps';
import { useProcessImageAsFrame } from '../../../../../../hooks/useProcessImageAsFrame';
import {
  selectActiveFrameId,
  selectCapturedScreenSize,
  selectPlayableFrames,
  setFocusFrameIndex,
  setIsScreenshotAsFrameUploading,
} from '../../../../../../store';
import { useTourEditorPageProps } from '../../../../TourEditorPageContext';
import DropdownItem from '../DropdownItem';

interface ICreateTourFromAnImage {
  selectedFrameIndex: number;
  tour: Tour;
  onClose: () => void;
}

const CreateTourFromAnImage = ({
  selectedFrameIndex,
  tour,
  onClose,
}: ICreateTourFromAnImage) => {
  const tourId = tour.id;
  const { uuid, fileServiceDynamicLink } = useTourEditorPageProps();
  const dispatch = useTourDispatch();
  const { trackEvent } = useAnalytics();
  const playableFrames = useTourSelector(selectPlayableFrames(uuid));
  const acitveFrameId = useTourSelector(selectActiveFrameId(uuid));
  const activeFrame = playableFrames.find(
    (frame) => frame.id === acitveFrameId
  );

  const optimalImageSize = useTourSelector(selectCapturedScreenSize(uuid));

  const { createImageFrame } = useProcessImageAsFrame({
    fileServiceDynamicLink,
  });

  const { getInputProps, getRootProps, open } = useImageProps({
    onImageAdded: (img) => {
      dispatch(
        setIsScreenshotAsFrameUploading({
          uuid,
          isUploading: true,
        })
      );
      dispatch(
        setFocusFrameIndex({
          uuid,
          frameIndex: selectedFrameIndex,
        })
      );
      onClose();
      createImageFrame({
        img: img as string,
        selectedFrameIndex,
        tourId: tourId,
        playableFrames,
        activeFrame,
        showTooltip: tour.draftSettings.showTooltips,
        fileServiceToken: tour.fileServiceToken,
      }).catch(() => {
        notify.error('Error creating frame');
        dispatch(
          setIsScreenshotAsFrameUploading({
            uuid,
            isUploading: false,
          })
        );
      });
    },
  });

  return (
    <div {...getRootProps()}>
      <DropdownItem
        title="Upload an image"
        subtitle="Use any image or screenshot as a frame"
        icon={<PhotoSquareOutlineIcon size={24} />}
        onClick={(e) => {
          trackEvent(AnalyticsTrackEventName.TOUR_CLICK_UPLOAD_SCREENSHOT, {
            location: 'tour_editor_adding_new_frames',
            tourId,
          });

          e.preventDefault();
          open();
        }}
        additionalInfo={
          optimalImageSize
            ? `Optimal image size ${optimalImageSize.width}x${optimalImageSize.height}. Use any image of these types: JPG, JPEG, PNG.`
            : undefined
        }
      />
      <input {...getInputProps()} />
    </div>
  );
};

export default CreateTourFromAnImage;
